import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Save } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { saveSettings, sendTestAlert } from "@/lib/api";

const DIRS = [
  { k: "inflow", label: "Wallet → Exchange" },
  { k: "outflow", label: "Exchange → Wallet" },
  { k: "exchange_to_exchange", label: "Exchange → Exchange" },
  { k: "wallet", label: "Wallet → Wallet" },
];

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const errMsg = (e) => e?.response?.data?.detail || e.message;

export default function SettingsPanel({ settings, onSaved, browserOn, onBrowser, soundOn, onSound, coins }) {
  const [form, setForm] = useState(null);
  const [token, setToken] = useState("");
  const [waKey, setWaKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (settings) setForm({ ...settings, custom: settings.min_usd / 1e6 }); }, [settings]);
  if (!form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { coins: form.coins, directions: form.directions, telegram_enabled: form.telegram_enabled,
        telegram_chat_id: form.telegram_chat_id, min_usd: Number(form.custom) * 1e6 };
      if (token) body.telegram_bot_token = token;
      if (waKey) body.whale_alert_api_key = waKey;
      onSaved(await saveSettings(body));
      setToken(""); setWaKey("");
      toast.success("Alert settings saved");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const test = async () => {
    try { await sendTestAlert(); toast.success("Test alert sent to Telegram"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="panel space-y-5 p-4" data-testid="alert-settings-form">
      <h2 className="font-display text-base font-semibold">Alert rules</h2>
      <div>
        <p className="overline mb-2">Min transfer size (USD, millions)</p>
        <div className="flex items-center gap-2">
          <Input data-testid="threshold-custom-input" type="number" min="0.5" step="0.5" value={form.custom}
            onChange={(e) => set({ custom: e.target.value })} className="w-28 border-[#2E3C54] bg-[#0F141C] font-mono" />
          <span className="font-mono text-xs text-slate-500">= ${Number(form.custom || 0).toLocaleString()}M</span>
        </div>
      </div>
      <div>
        <p className="overline mb-2">Coins</p>
        <div className="flex flex-wrap gap-1">
          {coins.map((c) => (
            <button key={c} data-testid={`alert-coin-${c}`} onClick={() => set({ coins: toggle(form.coins, c) })} className={`chip ${form.coins.includes(c) ? "active" : ""}`}>{c}</button>
          ))}
        </div>
      </div>
      <div>
        <p className="overline mb-2">Directions</p>
        <div className="flex flex-wrap gap-1">
          {DIRS.map((d) => (
            <button key={d.k} data-testid={`alert-direction-${d.k}`} onClick={() => set({ directions: toggle(form.directions, d.k) })} className={`chip !text-[11px] ${form.directions.includes(d.k) ? "active" : ""}`}>{d.label}</button>
          ))}
        </div>
      </div>
      <div className="space-y-3 border-t border-[#1E2738] pt-4">
        <Row label="Browser notifications" testid="browser-notifications-toggle" checked={browserOn} onChange={onBrowser} />
        <Row label="Sound beep" testid="sound-toggle" checked={soundOn} onChange={onSound} />
        <Row label="Telegram alerts" testid="telegram-toggle" checked={form.telegram_enabled} onChange={(v) => set({ telegram_enabled: v })} />
        <Input data-testid="telegram-token-input" type="password" placeholder={form.telegram_token_set ? "Bot token saved ••••" : "Telegram bot token (from @BotFather)"}
          value={token} onChange={(e) => setToken(e.target.value)} className="border-[#2E3C54] bg-[#0F141C] font-mono text-xs" />
        <Input data-testid="telegram-chat-id-input" placeholder="Telegram chat ID" value={form.telegram_chat_id}
          onChange={(e) => set({ telegram_chat_id: e.target.value })} className="border-[#2E3C54] bg-[#0F141C] font-mono text-xs" />
        {form.is_admin && (
          <Input data-testid="whale-alert-key-input" type="password" placeholder={form.whale_alert_key_set ? "Whale Alert key saved ••••" : "Whale Alert API key (admin, optional)"}
            value={waKey} onChange={(e) => setWaKey(e.target.value)} className="border-[#2E3C54] bg-[#0F141C] font-mono text-xs" />
        )}
      </div>
      <div className="flex gap-2">
        <button data-testid="save-settings-button" disabled={busy} onClick={save}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[#00E5FF] px-3 py-2 text-sm font-semibold text-[#080B10] transition-opacity hover:opacity-90 disabled:opacity-50">
          <Save className="h-4 w-4" /> Save
        </button>
        <button data-testid="test-alert-button" onClick={test}
          className="flex items-center gap-2 rounded-md border border-[#2E3C54] px-3 py-2 text-sm transition-colors hover:border-[#00E5FF]">
          <Send className="h-4 w-4" /> Test
        </button>
      </div>
    </section>
  );
}

const Row = ({ label, testid, checked, onChange }) => (
  <label className="flex items-center justify-between text-sm text-slate-300">
    {label}
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </label>
);
